//Frontend/src/unicodex_ui/pages/PortalForecastPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import ManufacturerNav from "../components/ManufacturerNav";
import portalAnalyticsApi from "../services/portalAnalyticsApi";
import "../styles/PortalBusiness.css";

export default function PortalForecastPage() {
  const links = useMemo(
    () => [
      { label: "Dashboard", to: "/portal/dashboard", perm: "portal.dashboard.read" },
      { label: "Shipments", to: "/portal/shipments", perm: "logistics.shipments.read" },
      { label: "Metered", to: "/portal/metered", perm: "meter.session.read" },
      { label: "Forecast", to: "/portal/forecast", perm: "analytics.forecast.read" },
      { label: "Compliance", to: "/portal/compliance", perm: "compliance.read" },
    ],
    []
  );

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [jobId, setJobId] = useState("");
  const [form, setForm] = useState({
    productId: "",
    branchId: "",
    daysBack: "30",
    windowDays: "7",
    horizonDays: "7",
  });

  const [results, setResults] = useState([]);

  async function loadResults(params = {}) {
    setLoading(true);
    setErr("");
    try {
      const res = await portalAnalyticsApi.listForecastResults(params);
      if (!res?.ok) throw new Error(res?.error || "Failed to load forecast results");
      setResults(Array.isArray(res.rows) ? res.rows : []);
    } catch (e) {
      setErr(e?.message || "Failed to load forecast results");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Initial load: none (wait for user to run or specify a jobId)
  }, []);

  async function runJob(e) {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const payload = {
        productId: form.productId ? Number(form.productId) : undefined,
        branchId: form.branchId ? Number(form.branchId) : undefined,
        daysBack: form.daysBack ? Number(form.daysBack) : undefined,
        windowDays: form.windowDays ? Number(form.windowDays) : undefined,
        horizonDays: form.horizonDays ? Number(form.horizonDays) : undefined,
      };

      const res = await portalAnalyticsApi.runForecastJob(payload);
      if (!res?.ok) throw new Error(res?.error || "Failed to run forecast job");

      if (res.jobId != null) setJobId(String(res.jobId));
      await loadResults({ jobId: res.jobId });
    } catch (e2) {
      setErr(e2?.message || "Failed to run forecast job");
    } finally {
      setLoading(false);
    }
  }

  async function onLoadByFilters() {
    setErr("");
    const params = {
      jobId: jobId ? Number(jobId) : undefined,
      productId: form.productId ? Number(form.productId) : undefined,
      branchId: form.branchId ? Number(form.branchId) : undefined,
      limit: 200,
      offset: 0,
    };
    await loadResults(params);
  }

  return (
    <div className="ucx-portal-page">
      <ManufacturerNav title="Forecast" links={links} />
      <div className="ucx-portal-content">
        <div className="ucx-portal-card">
          <h2 style={{ marginTop: 0 }}>Demand Forecast (MA7)</h2>
          {err ? <div className="ucx-portal-alert ucx-portal-alert-danger">{err}</div> : null}

          <form onSubmit={runJob} className="ucx-portal-form">
            <div className="ucx-portal-row" style={{ gap: 12 }}>
              <div style={{ width: 160 }}>
                <label className="ucx-portal-label">Job ID (optional)</label>
                <input className="ucx-portal-input" value={jobId} onChange={(e) => setJobId(e.target.value)} />
              </div>

              <div style={{ width: 160 }}>
                <label className="ucx-portal-label">Product ID</label>
                <input
                  className="ucx-portal-input"
                  value={form.productId}
                  onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value }))}
                  placeholder="optional"
                />
              </div>

              <div style={{ width: 160 }}>
                <label className="ucx-portal-label">Branch ID</label>
                <input
                  className="ucx-portal-input"
                  value={form.branchId}
                  onChange={(e) => setForm((p) => ({ ...p, branchId: e.target.value }))}
                  placeholder="optional"
                />
              </div>

              <div style={{ width: 140 }}>
                <label className="ucx-portal-label">Days back</label>
                <input
                  className="ucx-portal-input"
                  value={form.daysBack}
                  onChange={(e) => setForm((p) => ({ ...p, daysBack: e.target.value }))}
                />
              </div>

              <div style={{ width: 140 }}>
                <label className="ucx-portal-label">Window</label>
                <input
                  className="ucx-portal-input"
                  value={form.windowDays}
                  onChange={(e) => setForm((p) => ({ ...p, windowDays: e.target.value }))}
                />
              </div>

              <div style={{ width: 140 }}>
                <label className="ucx-portal-label">Horizon</label>
                <input
                  className="ucx-portal-input"
                  value={form.horizonDays}
                  onChange={(e) => setForm((p) => ({ ...p, horizonDays: e.target.value }))}
                />
              </div>
            </div>

            <div className="ucx-portal-row" style={{ gap: 10, marginTop: 12 }}>
              <button className="ucx-portal-btn ucx-portal-btn-primary" type="submit" disabled={loading}>
                Run forecast job
              </button>
              <button className="ucx-portal-btn" type="button" onClick={onLoadByFilters} disabled={loading}>
                Load results
              </button>
            </div>
          </form>
        </div>

        <div className="ucx-portal-card">
          <h3 style={{ marginTop: 0 }}>Results</h3>
          {loading ? <div className="ucx-muted">Loading…</div> : null}

          <div className="ucx-portal-table-wrap">
            <table className="ucx-portal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Branch</th>
                  <th>Qty</th>
                  <th>Method</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, idx) => (
                  <tr key={`${r.id ?? idx}`}>
                    <td>{r.forecast_date}</td>
                    <td>{r.product_id ?? "-"}</td>
                    <td>{r.branch_id ?? "-"}</td>
                    <td>{r.forecast_qty}</td>
                    <td>{r.method}</td>
                  </tr>
                ))}
                {!results.length ? (
                  <tr>
                    <td colSpan={5} className="ucx-muted">
                      No forecast rows yet. Run a job or load results by Job ID.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
